import { clearSessionTerminalContext } from './sessionTerminal';

/**
 * 테이블 오더 컨텍스트 판정 유틸리티
 *
 * 단일 출처 (박제 D — Single Source of Truth):
 * - `CheckoutPage` 의 orderType 결정
 * - `ApiClient.buildHeaders` 의 X-Session-Token 헤더 부착 분기
 * - 기타 테이블 컨텍스트 분기 모두 이 함수를 사용
 *
 * 시정 배경 (#367 codex 1R P0):
 * - sessionStorage.table_session_token 만으로 'table' 결정 시
 *   테이블 오더 종료 후 같은 브라우저로 픽업 매장 진입하면
 *   stale 토큰이 남아 픽업 주문이 테이블 컨텍스트로 오염됨.
 * - 해결: URL path `/table/{n}` 매칭과 토큰 존재 둘 다 만족할 때만
 *   테이블 컨텍스트로 판정 (path 기반 검증).
 */

const TABLE_PATH_PATTERN = /\/table\/([^/?#]+)/;
const RESTAURANT_TABLE_PATH_PATTERN =
  /^\/restaurant\/([^/?#]+)\/table\/([^/?#]+)(?:\/|$)/;
const TABLE_CHECKOUT_PATH_PATTERN = /^\/restaurant\/([^/?#]+)\/table\/([^/?#]+)\/checkout\/?$/;
const TABLE_SESSION_RECOVERY_KEY = 'olulo_table_session_recovery_v1';
const TABLE_SESSION_IDENTITY_KEY = 'olulo_table_session_identity_v1';
const TABLE_SESSION_STORE_ID_KEY = 'table_store_id';
/**
 * 좌석 참여자 토큰 — 분할결제에서 "이 사람" 을 가리킨다 (#814).
 *
 * 세션 토큰(`table_session_token`)이 "이 테이블" 이라면 이것은 "그 안의 누구" 다.
 * 세션이 바뀌거나 정리되면 **함께 무효**여야 하므로 런타임 키에 넣는다 — 남아 있으면
 * 다른 테이블에서 남의 좌석 신원으로 요청하게 된다.
 */
import {
  COMENSAL_ACCESS_SNAPSHOT_KEY,
  COMENSAL_DEVICE_TOKEN_KEY,
  SPLIT_PAYMENT_AVAILABLE_KEY,
  TABLE_SESSION_ENTRY_KEY,
  TABLE_SESSION_CHANGED_EVENT,
} from './sessionStorageKeys';

// 기존 import 경로를 깨지 않도록 그대로 재수출한다.
export { COMENSAL_DEVICE_TOKEN_KEY, TABLE_SESSION_CHANGED_EVENT };

const TABLE_SESSION_RUNTIME_KEYS = [
  'table_session_token',
  'table_number',
  'table_store_name',
  TABLE_SESSION_STORE_ID_KEY,
  COMENSAL_DEVICE_TOKEN_KEY,
  COMENSAL_ACCESS_SNAPSHOT_KEY,
  SPLIT_PAYMENT_AVAILABLE_KEY,
  TABLE_SESSION_ENTRY_KEY,
] as const;

/**
 * 테이블 세션 sessionStorage 토큰 변경(설정/삭제) 시 발행하는 이벤트.
 *
 * sessionStorage 는 같은 탭 내 변경에 대해 `storage` 이벤트를 발생시키지 않아
 * 반응형이 아니다. 토큰 기반 UI(예: useRestaurants 의 showTableOrderMenu)가
 * 토큰 clear/set 에 path 변경 없이도 즉시 반응하도록 명시 이벤트를 발행한다.
 *
 * 배경: useSessionTimer 가 BE 만료(401/410/404) 시 같은 `/table/` path 에서
 * clearTableSession() 만 호출하면, 토큰만 사라지고 토큰을 읽는 memo 가
 * 재계산되지 않아 테이블 모드가 stale 로 남던 문제를 차단한다.
 */

function emitTableSessionChanged(): void {
  if (typeof window === 'undefined') return;
  // getTableSessionFromStorage 의 방어적 정리(render 중 useState 초기화 등에서 호출 가능)
  // 경로까지 안전하도록 microtask 로 지연 — 다른 컴포넌트 렌더 중 setState 경고를 방지한다.
  queueMicrotask(() => window.dispatchEvent(new Event(TABLE_SESSION_CHANGED_EVENT)));
}

interface TableSessionRecoveryTable {
  id: number;
  table_number: string;
  store: {
    id: number;
    name: string;
  };
}

interface TableSessionRecoverySession {
  id: number;
  session_token?: string;
  expires_at: string;
}

interface TableSessionRecoveryRecord {
  token: string;
  sessionId: number;
  tableId: number;
  tableNumber: string;
  storeId: number;
  storeName: string;
  expiresAt: string;
}

interface TableSessionIdentityRecord {
  token: string;
  tableNumber: string;
  name: string;
  phone: string;
}

export interface PersistTableSessionInput {
  token: string;
  sessionId: number;
  expiresAt: string;
  table: TableSessionRecoveryTable;
  /** entry → joined 승격에서 같은 사람의 X-Comensal-Token을 보존한다. */
  preserveComensalIdentity?: boolean;
  /** QR/session 응답의 서버 권위 가용성. 제공되면 세션 토큰과 같은 전환에 저장한다. */
  splitPaymentAvailable?: boolean;
}

export interface PersistTableSessionEntryInput {
  entryToken: string;
  entryExpiresIn: number;
  activeSession: TableSessionRecoverySession;
  table: TableSessionRecoveryTable;
}

export interface TableSessionEntrySnapshot {
  entryToken: string;
  entryExpiresAt: number;
  sessionId: number;
  sessionExpiresAt: string;
  tableId: number;
  tableNumber: string;
  storeId: number;
  storeName: string;
}

export interface RestoreActiveTableSessionInput {
  activeSession: TableSessionRecoverySession;
  table: TableSessionRecoveryTable;
  /** QR의 entry 유무로 판정한 현재 서버 split policy. */
  splitPaymentAvailable?: boolean;
}

export interface TableSessionIdentity {
  name: string;
  phone: string;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function removePersistedTableSession(): void {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.removeItem(TABLE_SESSION_RECOVERY_KEY);
  } catch {
    // localStorage 접근 실패는 세션 cleanup 자체를 막지 않는다.
  }
}

function removeTableSessionIdentity(): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(TABLE_SESSION_IDENTITY_KEY);
  } catch {
    // sessionStorage 접근 실패는 테이블 세션 cleanup 자체를 막지 않는다.
  }
}

function readPersistedTableSession(): TableSessionRecoveryRecord | null {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(TABLE_SESSION_RECOVERY_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<TableSessionRecoveryRecord>;
    if (
      !isNonEmptyString(parsed.token) ||
      typeof parsed.sessionId !== 'number' ||
      typeof parsed.tableId !== 'number' ||
      !isNonEmptyString(parsed.tableNumber) ||
      typeof parsed.storeId !== 'number' ||
      !isNonEmptyString(parsed.storeName) ||
      !isNonEmptyString(parsed.expiresAt)
    ) {
      return null;
    }

    return parsed as TableSessionRecoveryRecord;
  } catch {
    return null;
  }
}

function writeSessionStorageTableSession(
  record: TableSessionRecoveryRecord,
  splitPaymentAvailable?: boolean,
): void {
  if (typeof window === 'undefined') return;

  try {
    // ★ 좌석 신원은 **이 세션에 매인 값**이다. 다른 테이블 세션으로 갈아타는데 남겨 두면
    //   테이블 A 에서 받은 `X-Comensal-Token` 이 테이블 B 요청에 실린다.
    //   토큰이 바뀔 때만 지운다 — 같은 세션 재기록(복구 경로)에서 합류 상태를 잃으면
    //   손님이 이유 없이 다시 스캔해야 한다.
    const entry = readTableSessionEntry();
    const promotesMatchingEntry = entry !== null
      && entry.sessionId === record.sessionId
      && entry.tableId === record.tableId
      && entry.storeId === record.storeId
      && entry.tableNumber === record.tableNumber;
    const previousToken = window.sessionStorage.getItem('table_session_token');
    const changesSession = previousToken !== record.token;
    if (changesSession && !promotesMatchingEntry) {
      window.sessionStorage.removeItem(COMENSAL_DEVICE_TOKEN_KEY);
      window.sessionStorage.removeItem(COMENSAL_ACCESS_SNAPSHOT_KEY);
      if (splitPaymentAvailable === undefined) {
        window.sessionStorage.removeItem(SPLIT_PAYMENT_AVAILABLE_KEY);
      }
    }

    window.sessionStorage.setItem('table_session_token', record.token);
    window.sessionStorage.setItem('table_number', record.tableNumber);
    window.sessionStorage.setItem('table_store_name', record.storeName);
    window.sessionStorage.setItem(TABLE_SESSION_STORE_ID_KEY, String(record.storeId));
    if (splitPaymentAvailable !== undefined) {
      window.sessionStorage.setItem(
        SPLIT_PAYMENT_AVAILABLE_KEY,
        splitPaymentAvailable ? 'true' : 'false',
      );
    }
  } catch (error) {
    for (const key of TABLE_SESSION_RUNTIME_KEYS) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // 부분 세션 cleanup 실패가 원래 storage 오류를 가리지 않도록 한다.
      }
    }
    throw error;
  }

  clearSessionTerminalContext();
  const identity = readTableSessionIdentity();
  if (
    identity !== null &&
    (identity.token !== record.token || identity.tableNumber !== record.tableNumber)
  ) {
    removeTableSessionIdentity();
  }
  emitTableSessionChanged();
}

function readTableSessionEntry(): TableSessionEntrySnapshot | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(TABLE_SESSION_ENTRY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TableSessionEntrySnapshot>;
    if (
      !isNonEmptyString(parsed.entryToken)
      || typeof parsed.entryExpiresAt !== 'number'
      || !Number.isSafeInteger(parsed.sessionId)
      || !isNonEmptyString(parsed.sessionExpiresAt)
      || !Number.isSafeInteger(parsed.tableId)
      || !isNonEmptyString(parsed.tableNumber)
      || !Number.isSafeInteger(parsed.storeId)
      || !isNonEmptyString(parsed.storeName)
    ) {
      window.sessionStorage.removeItem(TABLE_SESSION_ENTRY_KEY);
      return null;
    }
    if (parsed.entryExpiresAt <= Date.now()) {
      clearTableSessionEntry();
      return null;
    }
    return parsed as TableSessionEntrySnapshot;
  } catch {
    try {
      window.sessionStorage.removeItem(TABLE_SESSION_ENTRY_KEY);
    } catch {
      // 읽기 실패는 entry 부재로 닫는다.
    }
    return null;
  }
}

/** QR이 발급한 짧은 entry를 저장하고, 주문 권한 없이 테이블 메뉴 열람만 연다. */
export function persistTableSessionEntry(input: PersistTableSessionEntryInput): void {
  if (
    typeof window === 'undefined'
    || !isNonEmptyString(input.entryToken)
    || !Number.isFinite(input.entryExpiresIn)
    || input.entryExpiresIn <= 0
  ) return;

  const snapshot: TableSessionEntrySnapshot = {
    entryToken: input.entryToken,
    entryExpiresAt: Date.now() + Math.floor(input.entryExpiresIn * 1000),
    sessionId: input.activeSession.id,
    sessionExpiresAt: input.activeSession.expires_at,
    tableId: input.table.id,
    tableNumber: input.table.table_number,
    storeId: input.table.store.id,
    storeName: input.table.store.name,
  };

  clearTableSession();
  window.sessionStorage.setItem(TABLE_SESSION_ENTRY_KEY, JSON.stringify(snapshot));
  window.sessionStorage.setItem('table_number', snapshot.tableNumber);
  window.sessionStorage.setItem('table_store_name', snapshot.storeName);
  window.sessionStorage.setItem(TABLE_SESSION_STORE_ID_KEY, String(snapshot.storeId));
  window.sessionStorage.setItem(SPLIT_PAYMENT_AVAILABLE_KEY, 'true');
  emitTableSessionChanged();
}

function writeBoundTableSessionEntry(
  entryToken: string,
  entryExpiresAt: string,
  deviceToken?: string,
): boolean {
  const entry = readTableSessionEntry();
  const expiresAt = Date.parse(entryExpiresAt);
  if (
    entry === null
    || entry.entryToken !== entryToken
    || !Number.isFinite(expiresAt)
    || expiresAt <= Date.now()
    || (deviceToken !== undefined && !isNonEmptyString(deviceToken))
  ) return false;

  try {
    // scan 성공 시 entry 절대 만료와 device token을 같은 storage transaction에서 쓴다.
    // 둘 중 하나만 남으면 다음 reload가 잘못된 unbound 상태로 복구될 수 있다.
    window.sessionStorage.setItem(
      TABLE_SESSION_ENTRY_KEY,
      JSON.stringify({ ...entry, entryExpiresAt: expiresAt }),
    );
    if (deviceToken !== undefined) {
      window.sessionStorage.setItem(COMENSAL_DEVICE_TOKEN_KEY, deviceToken);
    }
  } catch {
    return false;
  }

  return true;
}

/** scan이 device에 bind한 entry의 절대 만료와 device token을 원자 갱신한다. */
export function bindTableSessionEntry(
  entryToken: string,
  deviceToken: string,
  entryExpiresAt: string,
): boolean {
  const bound = writeBoundTableSessionEntry(entryToken, entryExpiresAt, deviceToken);
  if (bound) emitTableSessionChanged();
  return bound;
}

/**
 * pending/current 응답의 서버 권위 절대 만료를 저장한다(반복 current로 자체 연장하지 않음).
 *
 * 만료 메타데이터만 갱신할 때는 테이블 문맥 변경 이벤트를 발행하지 않는다. 이 이벤트를
 * 구독하는 RestaurantPage가 다시 마운트되면 current → expiry 저장 → remount가 순환해
 * 매장/좌석 API를 폭주시킬 수 있다. 실제 문맥 전환인 bind/persist/promote/clear만 알린다.
 */
export function refreshTableSessionEntryExpiry(
  entryToken: string,
  entryExpiresAt: string,
): boolean {
  return writeBoundTableSessionEntry(entryToken, entryExpiresAt);
}

export function clearTableSessionEntry(): void {
  if (typeof window === 'undefined') return;
  let hadEntry = false;
  try {
    hadEntry = window.sessionStorage.getItem(TABLE_SESSION_ENTRY_KEY) !== null;
    window.sessionStorage.removeItem(TABLE_SESSION_ENTRY_KEY);
    // live 세션이 이미 승격됐다면 그 런타임과 신원은 건드리지 않는다.
    if (!window.sessionStorage.getItem('table_session_token')) {
      window.sessionStorage.removeItem('table_number');
      window.sessionStorage.removeItem('table_store_name');
      window.sessionStorage.removeItem(TABLE_SESSION_STORE_ID_KEY);
      window.sessionStorage.removeItem(COMENSAL_DEVICE_TOKEN_KEY);
      window.sessionStorage.removeItem(COMENSAL_ACCESS_SNAPSHOT_KEY);
      window.sessionStorage.removeItem(SPLIT_PAYMENT_AVAILABLE_KEY);
    }
  } catch {
    return;
  }
  if (hadEntry) emitTableSessionChanged();
}

export function getValidatedTableSessionEntryForRoute(
  pathname: string,
): TableSessionEntrySnapshot | null {
  const match = pathname.match(RESTAURANT_TABLE_PATH_PATTERN);
  if (!match) return null;
  const routeStoreId = Number(decodeRouteSegment(match[1]));
  const routeTableNumber = decodeRouteSegment(match[2]);
  const entry = readTableSessionEntry();
  if (
    !Number.isSafeInteger(routeStoreId)
    || entry === null
    || entry.storeId !== routeStoreId
    || entry.tableNumber !== routeTableNumber
  ) return null;
  return entry;
}

/** entry current/join 응답의 one-time session_token을 실제 테이블 세션으로 승격한다. */
export function promoteTableSessionEntry(sessionToken: string): boolean {
  const entry = readTableSessionEntry();
  if (entry === null || !isNonEmptyString(sessionToken)) return false;

  persistTableSession({
    token: sessionToken,
    sessionId: entry.sessionId,
    expiresAt: entry.sessionExpiresAt,
    table: {
      id: entry.tableId,
      table_number: entry.tableNumber,
      store: { id: entry.storeId, name: entry.storeName },
    },
    preserveComensalIdentity: true,
  });
  clearTableSessionEntry();
  return true;
}

function readTableSessionIdentity(): TableSessionIdentityRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(TABLE_SESSION_IDENTITY_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<TableSessionIdentityRecord>;
    if (
      !isNonEmptyString(parsed.token) ||
      !isNonEmptyString(parsed.tableNumber) ||
      typeof parsed.name !== 'string' ||
      typeof parsed.phone !== 'string'
    ) {
      removeTableSessionIdentity();
      return null;
    }

    return parsed as TableSessionIdentityRecord;
  } catch {
    removeTableSessionIdentity();
    return null;
  }
}

/**
 * QR 테이블 세션 시작/복구 시 현재 브라우저에 세션 컨텍스트를 보존한다.
 *
 * sessionStorage 는 기존 런타임 컨텍스트에 계속 사용하고, localStorage 는 탭/앱 종료 후
 * 같은 활성 세션 QR을 다시 스캔했을 때 복구할 수 있는 최소 스냅샷으로만 사용한다.
 */
export function persistTableSession(input: PersistTableSessionInput): void {
  if (!isNonEmptyString(input.token)) return;

  if (!input.preserveComensalIdentity && typeof window !== 'undefined') {
    // 일반 새 세션 시작은 이전 entry 신원과 무관하다. write 전에 지워야 새 세션이
    // entry의 X-Comensal-Token을 보존하는 것으로 오인하지 않는다.
    window.sessionStorage.removeItem(TABLE_SESSION_ENTRY_KEY);
  }

  const record: TableSessionRecoveryRecord = {
    token: input.token,
    sessionId: input.sessionId,
    tableId: input.table.id,
    tableNumber: input.table.table_number,
    storeId: input.table.store.id,
    storeName: input.table.store.name,
    expiresAt: input.expiresAt,
  };

  writeSessionStorageTableSession(record, input.splitPaymentAvailable);

  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(TABLE_SESSION_RECOVERY_KEY, JSON.stringify(record));
  } catch {
    // 현재 record 저장 실패 시 과거 record가 이후 복구 권위로 오인되지 않도록 제거한다.
    // durable recovery 실패와 cleanup 실패 모두 현재 4키 runtime 진입은 막지 않는다.
    removePersistedTableSession();
  }
}

/**
 * QR 재스캔 시 BE가 보고한 활성 세션과 로컬 스냅샷이 같은 세션이면 토큰을 복구한다.
 * BE가 active_session.session_token 을 내려주기 시작하면 그 값을 우선 사용한다.
 */
export function restoreActiveTableSessionFromStorage(
  input: RestoreActiveTableSessionInput
): string | null {
  const directToken = input.activeSession.session_token;
  if (isNonEmptyString(directToken)) {
    persistTableSession({
      token: directToken,
      sessionId: input.activeSession.id,
      expiresAt: input.activeSession.expires_at,
      table: input.table,
      splitPaymentAvailable: input.splitPaymentAvailable,
    });
    return directToken;
  }

  const record = readPersistedTableSession();
  if (!record) return null;

  const expiresAt = Date.parse(record.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    removePersistedTableSession();
    return null;
  }

  if (
    record.sessionId !== input.activeSession.id ||
    record.tableId !== input.table.id ||
    record.tableNumber !== input.table.table_number ||
    record.storeId !== input.table.store.id
  ) {
    return null;
  }

  writeSessionStorageTableSession(record, input.splitPaymentAvailable);
  return record.token;
}

/**
 * 주어진 pathname 에서 테이블 번호 추출.
 * @param pathname - URL pathname (예: `/restaurant/123/table/A1`)
 * @returns 테이블 번호 (decode 됨), 매칭 실패 시 undefined
 */
export function extractTableNumberFromPath(pathname: string): string | undefined {
  const match = pathname.match(TABLE_PATH_PATTERN);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * 현재 컨텍스트가 유효한 테이블 오더 컨텍스트인지 판정.
 *
 * 조건 (둘 다 만족해야 true):
 * 1. URL path 가 `/table/{n}` 패턴 매칭
 * 2. sessionStorage 에 비어있지 않은 `table_session_token` 존재
 *
 * @param pathname - URL pathname
 * @param sessionToken - sessionStorage 에서 읽은 토큰 (null 가능)
 * @returns 두 조건 모두 만족 시 true
 */
export function isValidTableContext(
  pathname: string,
  sessionToken: string | null
): boolean {
  if (sessionToken === null || sessionToken === '') return false;
  return extractTableNumberFromPath(pathname) !== undefined;
}

/**
 * SSR-safe 래퍼: window 가 있으면 현재 location/sessionStorage 기준으로 판정.
 * 서버/window 미지원 환경은 false (pickup 폴백).
 */
export function isCurrentTableContext(): boolean {
  if (typeof window === 'undefined') return false;
  const sessionToken = window.sessionStorage.getItem('table_session_token');
  return isValidTableContext(window.location.pathname, sessionToken);
}

/**
 * 현재 path 기준으로 테이블 번호 추출 (SSR-safe).
 */
export function getCurrentTableNumber(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return extractTableNumberFromPath(window.location.pathname);
}

/**
 * 체크아웃 흐름 전용 — sessionStorage 기반 테이블 세션 컨텍스트 조회.
 *
 * 배경 (#1430 — admin BE P0 으로 보고된 "테이블 오더가 pickup 으로 저장" 의 FE root cause):
 * - 카트 → `navigate('/checkout')` 시 URL 이 `/checkout` 으로 바뀜.
 * - `isValidTableContext` 는 path `/table/{n}` 매칭 강제 → checkout path 에서 false.
 * - 결과: `CheckoutPage.orderType` 가 'pickup' 으로 silent fallback,
 *   `X-Session-Token` 도 전송 안 함 → BE 는 정상 pickup 으로 저장 (table_session_id NULL).
 *
 * 단일 출처 박제 정합 (박제 D):
 * - `isValidTableContext` 는 path 가 있는 컨텍스트 (메뉴/카트 모달) 전용 — 보존.
 * - 본 helper 는 path 가 사라지는 *체크아웃 흐름 전용* — 별도 진입점.
 * - 둘 다 stale token 오염 방지 — token 단독 판정 절대 금지.
 *
 * Defense-in-depth — sessionStorage 3 키 모두 존재해야 true:
 *   1. `table_session_token` (QRVerifyPage 가 저장)
 *   2. `table_number` (PickupMenu 가 mode='table' 진입 시 저장 — 픽업 진입 시 clear)
 *   3. `table_store_name` (PickupMenu 가 mode='table' 진입 시 저장 — 픽업 진입 시 clear)
 *
 * 픽업 매장 진입 시 PickupMenu 가 (2)(3) 을 clear 하므로 stale token 만 남아도 false 반환됨.
 *
 * @returns 3 키 모두 존재 시 객체, 아니면 null
 */
export interface CheckoutTableSession {
  token: string;
  tableNumber: string;
  storeName: string;
}

export function getTableSessionFromStorage(): CheckoutTableSession | null {
  if (typeof window === 'undefined') return null;
  let token: string | null;
  let tableNumber: string | null;
  let storeName: string | null;
  let storeId: string | null;

  try {
    token = window.sessionStorage.getItem('table_session_token');
    tableNumber = window.sessionStorage.getItem('table_number');
    storeName = window.sessionStorage.getItem('table_store_name');
    storeId = window.sessionStorage.getItem(TABLE_SESSION_STORE_ID_KEY);
  } catch {
    return null;
  }

  // R2 codex P1 — defense-in-depth: token 부재 시 잔존 2 키 자동 cleanup.
  // SessionExpiredPage / SessionControlBar / 401 handler 등이 token 만 단독 제거 시
  // (table_number / table_store_name) 가 stale 로 남는 silent stale 시나리오 차단.
  // 다음 매장 진입 + 새 token 결합 → 잘못된 컨텍스트 오염 방지.
  if (!token || !tableNumber || !storeName) {
    const entry = readTableSessionEntry();
    if (
      !token
      && entry !== null
      && entry.tableNumber === tableNumber
      && entry.storeName === storeName
      && String(entry.storeId) === storeId
    ) {
      return null;
    }
    if (token || tableNumber || storeName || storeId) {
      // 부분 잔존 시에만 cleanup (전부 비어있으면 noop)
      for (const key of TABLE_SESSION_RUNTIME_KEYS) {
        try {
          window.sessionStorage.removeItem(key);
        } catch {
          // checkout helper의 방어적 cleanup 실패는 null 판정을 바꾸지 않는다.
        }
      }
      removeTableSessionIdentity();
      // 토큰이 제거됐으므로 토큰 기반 UI 가 반응하도록 변경 이벤트 발행(microtask 지연).
      emitTableSessionChanged();
    }
    return null;
  }
  return { token, tableNumber, storeName };
}

export interface RouteTableSessionSnapshot extends CheckoutTableSession {
  storeId: number;
}

function readRouteRuntimeTableSession(): RouteTableSessionSnapshot | null {
  if (typeof window === 'undefined') return null;

  try {
    const token = window.sessionStorage.getItem('table_session_token');
    const tableNumber = window.sessionStorage.getItem('table_number');
    const storeName = window.sessionStorage.getItem('table_store_name');
    const rawStoreId = window.sessionStorage.getItem(TABLE_SESSION_STORE_ID_KEY);

    if (
      !isNonEmptyString(token) ||
      !isNonEmptyString(tableNumber) ||
      !isNonEmptyString(storeName) ||
      !isNonEmptyString(rawStoreId)
    ) {
      return null;
    }

    const storeId = Number(rawStoreId);
    if (!Number.isSafeInteger(storeId)) return null;

    return { token, tableNumber, storeName, storeId };
  } catch {
    return null;
  }
}

function decodeRouteSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * 테이블 메뉴 진입 전용 route-aware 세션 스냅샷.
 *
 * 런타임 4키(token/table/store name/store id)가 모두 존재하고 URL의 store/table과
 * 정확히 일치해야 한다. 이 4키는 QR persist 또는 검증된 durable restore만 기록하므로
 * 현재 route의 권위 스냅샷이다. localStorage record는 선택적 복구 metadata이며 접근
 * 실패·오래된 token/table/store/expiry 어느 것도 완전한 live runtime을 거부하거나
 * 대체할 수 없다. 서버 GET/poll/extend가 stale durable record를 후속 정리한다.
 */
export function getValidatedTableSessionSnapshotForRoute(
  pathname: string,
): RouteTableSessionSnapshot | null {
  const match = pathname.match(RESTAURANT_TABLE_PATH_PATTERN);
  if (!match) return null;

  const routeStoreId = Number(decodeRouteSegment(match[1]));
  const routeTableNumber = decodeRouteSegment(match[2]);
  const runtime = readRouteRuntimeTableSession();
  if (
    !Number.isSafeInteger(routeStoreId) ||
    !runtime ||
    runtime.tableNumber !== routeTableNumber ||
    runtime.storeId !== routeStoreId
  ) {
    return null;
  }

  return runtime;
}

/**
 * 서버 권위 active 응답의 만료 시각을 동일 세션의 durable 복구 record에 반영한다.
 *
 * token/sessionId가 일치할 때만 expiry를 쓰고 값이 같으면 noop 한다. 불일치 record는
 * 이후 잘못 복원되지 않도록 제거한다. 저장소 접근/정리 실패는 현재 세션 타이머 동작을
 * 방해하지 않도록 false로 흡수한다.
 */
export function syncTableSessionRecoveryExpiry(input: {
  token: string;
  sessionId: number;
  expiresAt: string;
}): boolean {
  if (
    !isNonEmptyString(input.token) ||
    !Number.isSafeInteger(input.sessionId) ||
    !isNonEmptyString(input.expiresAt) ||
    !Number.isFinite(Date.parse(input.expiresAt))
  ) {
    return false;
  }

  const storage = getLocalStorage();
  if (!storage) return false;

  try {
    const record = readPersistedTableSession();
    if (!record) return false;

    if (record.token !== input.token || record.sessionId !== input.sessionId) {
      removePersistedTableSession();
      return false;
    }

    if (record.expiresAt === input.expiresAt) {
      return false;
    }

    storage.setItem(
      TABLE_SESSION_RECOVERY_KEY,
      JSON.stringify({ ...record, expiresAt: input.expiresAt }),
    );
    return true;
  } catch {
    return false;
  }
}

function extractTableCheckoutPathContext(pathname: string): {
  storeId: number | null;
  tableNumber: string;
} | null {
  const match = pathname.match(TABLE_CHECKOUT_PATH_PATTERN);
  if (!match) return null;

  const rawStoreId = match[1] ?? '';
  const rawTableNumber = match[2] ?? '';
  const storeId = Number(rawStoreId);
  let tableNumber = rawTableNumber;

  try {
    tableNumber = decodeURIComponent(rawTableNumber);
  } catch {
    // 잘못 인코딩된 path 는 extractTableNumberFromPath 와 동일하게 raw 값을 사용한다.
  }

  return {
    storeId: Number.isFinite(storeId) ? storeId : null,
    tableNumber,
  };
}

/**
 * 체크아웃 라우트 전용 테이블 세션 조회.
 *
 * - 레거시 `/checkout` 은 기존 3-key sessionStorage 검증을 그대로 사용한다.
 * - 표준 `/restaurant/{id}/table/{n}/checkout` 은 URL table number 와 sessionStorage
 *   table_number 일치를 추가로 강제해 stale token + 직접 URL 조합을 차단한다.
 * - 같은 token 의 localStorage 복구 record 가 있으면 store/table 도 일치해야 한다.
 */
export function getCheckoutTableSessionFromStorage(pathname: string): CheckoutTableSession | null {
  const session = getTableSessionFromStorage();
  if (!session) return null;

  const pathContext = extractTableCheckoutPathContext(pathname);
  if (!pathContext) return session;

  if (session.tableNumber !== pathContext.tableNumber) {
    return null;
  }

  const record = readPersistedTableSession();
  if (!record || record.token !== session.token) return session;

  if (
    record.tableNumber !== pathContext.tableNumber ||
    (pathContext.storeId !== null && record.storeId !== pathContext.storeId)
  ) {
    return null;
  }

  return session;
}

/**
 * 테이블 체크아웃 F-07: 같은 QR 세션 내 재주문 시 선택 입력한 이름/전화만 재사용한다.
 *
 * 저장 범위는 sessionStorage 로 제한하고, 현재 table session token + tableNumber 가
 * 모두 일치할 때만 반환한다. 다른 테이블/새 세션의 선택 입력값이 섞이는 것을 막기 위함이다.
 */
export function getTableSessionIdentity(
  tableSession: CheckoutTableSession | null
): TableSessionIdentity | null {
  if (!tableSession) return null;

  const identity = readTableSessionIdentity();
  if (!identity) return null;

  if (identity.token !== tableSession.token || identity.tableNumber !== tableSession.tableNumber) {
    removeTableSessionIdentity();
    return null;
  }

  return {
    name: identity.name,
    phone: identity.phone,
  };
}

export function persistTableSessionIdentity(
  tableSession: CheckoutTableSession,
  identity: TableSessionIdentity
): void {
  if (typeof window === 'undefined') return;

  const trimmedIdentity = {
    token: tableSession.token,
    tableNumber: tableSession.tableNumber,
    name: identity.name.trim(),
    phone: identity.phone.trim(),
  };

  try {
    if (!trimmedIdentity.name && !trimmedIdentity.phone) {
      removeTableSessionIdentity();
      return;
    }

    window.sessionStorage.setItem(TABLE_SESSION_IDENTITY_KEY, JSON.stringify(trimmedIdentity));
  } catch {
    // 선택 입력 재사용 실패는 체크아웃 제출을 막지 않는다.
  }
}

/**
 * 결제 결과 등 path 가 사라진 화면에서 "테이블로 복귀" 네비게이션에 필요한 컨텍스트 (#657).
 *
 * 배경:
 * - 3DS 거절 복귀(`/payment/result?source=3ds&outcome=declined`)는 orderId 가 없어
 *   상태 재조회가 불가하고, path 에도 `/table/{n}` 이 없어 `isValidTableContext` 불가.
 * - 복귀 경로 `/restaurant/{storeId}/table/{tableNumber}` 에는 storeId 가 필요한데,
 *   checkout의 레거시 3키 반환값에는 storeId가 없음 → localStorage 복구 record가 출처.
 *
 * 판정 (#670 — record 단일 SSOT + sessionStorage 불일치 거부):
 * 1. localStorage 복구 record 존재 + 형태 검증 (`readPersistedTableSession`)
 * 2. record.expiresAt 미래 — 로컬 만료 판정 (서버 검증은 이동 후 대상 페이지가 수행)
 * 3. sessionStorage 3 키가 *존재하면* record.token 과 일치해야 함 — 다른 세션의
 *    stale record 결합 차단 (#658 가드 보존)
 * 4. sessionStorage 3 키 *부재 시 record 단독 폴백* (#670) — 픽업 매장 진입(PickupMenu
 *    clear) 등 비종료성 화면 이동이 sessionStorage 런타임 컨텍스트를 비워도, 같은 기기의
 *    localStorage record 는 동일 신뢰 수준이므로 복귀 컨텍스트는 유지된다.
 *
 * 종료성 경로의 record 삭제 근거 (#670 codex I1 — 폴백이 좀비 세션이 되지 않는 이유):
 * - 사용자 명시 종료: src/components/session/SessionControlBar.tsx:113 → clearTableSession()
 * - 만료 화면 진입: src/pages/SessionExpiredPage.tsx:24 → clearTableSession()
 * - 서버 권위 종료/무효: src/hooks/useSessionTimer.ts — fetchSession 의
 *   completed/inactive/expired 200 응답 또는 401/410/404 분기 → clearTableSession().
 *   복원/폴백된 토큰도 테이블 페이지 재진입 시 초기 fetch + 60s 폴링에서 종료/무효
 *   판명되면 즉시 제거된다.
 * → clearTableSession() (본 파일 하단) 은 sessionStorage 4 키 + removePersistedTableSession()
 *   (localStorage record) 를 함께 제거하므로 위 세 경로 이후 본 폴백은 성립하지 않는다.
 *
 * fail-safe: 어느 하나라도 불만족 시 null (호출 측은 기존 홈 동작 유지).
 * 만료 record 는 감지 즉시 제거한다 (#670 codex C1) — restoreTableSessionFromRecord 와
 * 공통 정책. 잔존 시 폴백 소비처(글로벌 바 30s tick 등)가 만료 record 를 무한 재판정한다.
 */
export interface TableSessionReturnContext {
  storeId: number;
  tableNumber: string;
  /** 복구 record 의 매장명 — 글로벌 세션 레이어(#660/#670) 표시 출처 (record SSOT) */
  storeName: string;
  /**
   * 복구 record 의 만료 시각 (ISO 8601) — 글로벌 세션 레이어(#660)가 만료 시각 기준
   * 정확한 제거 타이머를 설정하는 입력. 반환 시점에 미래임이 보장된다 (판정 2 통과).
   */
  expiresAt: string;
}

export function getTableSessionReturnContext(): TableSessionReturnContext | null {
  const record = readPersistedTableSession();
  if (!record) return null;

  const expiresAt = Date.parse(record.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    // #670 codex C1 — 만료 감지 즉시 record cleanup (restoreTableSessionFromRecord 공통).
    removePersistedTableSession();
    return null;
  }

  // sessionStorage 컨텍스트가 존재하면 token 일치 강제 (#658 불일치 거부 보존).
  // 부재(부분 잔존은 읽기 측 cleanup 후 부재로 수렴)면 record 단독 폴백 (#670).
  const session = getTableSessionFromStorage();
  if (session && session.token !== record.token) return null;

  return {
    storeId: record.storeId,
    tableNumber: record.tableNumber,
    storeName: record.storeName,
    expiresAt: record.expiresAt,
  };
}

/**
 * localStorage 복구 record로 sessionStorage 4키 런타임 컨텍스트를 복원/승격 (#670).
 *
 * 배경:
 * - 픽업 매장 진입(PickupMenu) 등 비종료성 이동이 sessionStorage 런타임 키를 비우면,
 *   테이블 페이지(`/restaurant/{id}/table/{n}`) 재진입 시 RestaurantPage 의
 *   토큰 조기 차단에 걸려 "세션 만료" 에러가 떴다 — 활성 record 가 살아있는데도.
 * - QR 재스캔 복구(restoreActiveTableSessionFromStorage)는 BE active_session 응답이
 *   필요하지만, 본 함수는 *복귀 네비게이션* 전용 — 같은 기기 localStorage record 는
 *   sessionStorage 와 동일 신뢰 수준이고, 복원 직후 useSessionTimer 의 BE 조회가
 *   서버 권위 검증을 수행한다 (completed/inactive/expired 200 응답 또는 401/410/404 →
 *   clearTableSession() = 4 키 + record 동시 제거, #670 codex I1 근거 박제).
 *
 * 가드 (모두 만족해야 복원):
 * 1. runtime token 부재면 durable-only 복원을 허용한다.
 * 2. runtime token이 있으면 완전한 legacy 3키여야 하고 record token/table도 같아야 한다.
 *    다른 durable token으로 현재 token을 교체하지 않는다.
 * 3. record 존재 + 형태 검증 + 미만료 (만료 record 는 cleanup 후 거부)
 * 4. record 의 storeId/tableNumber 가 복귀 대상과 일치해야 trusted storeId를 기록한다.
 *    URL 값만으로 storeId를 추론하지 않는다.
 *
 * @returns 복원 성공 여부 (false 시 호출 측은 기존 에러/만료 동선 유지)
 */
export function restoreTableSessionFromRecord(expected: {
  storeId: number;
  tableNumber: string;
}): boolean {
  if (typeof window === 'undefined') return false;

  let runtimeToken: string | null;
  let runtimeTableNumber: string | null;
  let runtimeStoreName: string | null;
  let runtimeStoreId: string | null;
  try {
    runtimeToken = window.sessionStorage.getItem('table_session_token');
    runtimeTableNumber = window.sessionStorage.getItem('table_number');
    runtimeStoreName = window.sessionStorage.getItem('table_store_name');
    runtimeStoreId = window.sessionStorage.getItem(TABLE_SESSION_STORE_ID_KEY);
  } catch {
    return false;
  }

  const hasLegacyRuntimeToken = isNonEmptyString(runtimeToken ?? '');
  if (
    hasLegacyRuntimeToken &&
    (
      !isNonEmptyString(runtimeTableNumber ?? '') ||
      !isNonEmptyString(runtimeStoreName ?? '') ||
      isNonEmptyString(runtimeStoreId ?? '') ||
      runtimeTableNumber !== expected.tableNumber
    )
  ) {
    return false;
  }

  const record = readPersistedTableSession();
  if (!record) return false;

  const expiresAt = Date.parse(record.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    // 만료 record 즉시 cleanup — getTableSessionReturnContext 와 공통 정책 (#670 codex C1)
    removePersistedTableSession();
    return false;
  }

  if (record.storeId !== expected.storeId || record.tableNumber !== expected.tableNumber) {
    return false;
  }
  if (hasLegacyRuntimeToken && record.token !== runtimeToken) return false;

  try {
    writeSessionStorageTableSession(record);
    return true;
  } catch {
    return false;
  }
}

/**
 * 테이블 세션 sessionStorage 4 키 일괄 제거 — 단일 출처 cleanup helper.
 *
 * 배경 (#1430 R2 codex P1 — silent stale 차단):
 * - 기존 token 만 단독 `removeItem` 하는 코드 경로 (SessionExpiredPage, SessionControlBar 등)
 *   가 잔존 시 (table_number / table_store_name) 2 키가 stale 로 남음.
 * - 다음 매장 진입 + 새 token 결합 시 잘못된 컨텍스트 오염 가능.
 *
 * 본 helper 는 4 키를 함께 제거 — 박제 D 단일 출처 정합.
 *
 * 사용 위치 (광역 grep 결과):
 * - `SessionExpiredPage.tsx` — 세션 만료 후 홈 이동
 * - `SessionControlBar.tsx` — 사용자 명시 종료
 * - 서버 권위 종료/무효 fetch handler / useSessionTimer 의 만료 처리 등
 *
 * @see getTableSessionFromStorage — 읽기 측 defensive cleanup 도 함께 적용 (defense-in-depth)
 */
export function clearTableSession(): void {
  if (typeof window === 'undefined') return;

  const storage = getLocalStorage();
  let hadPersistedRecord = false;
  try {
    hadPersistedRecord = storage !== null && storage.getItem(TABLE_SESSION_RECOVERY_KEY) !== null;
  } catch {
    hadPersistedRecord = false;
  }

  let hadSessionState = hadPersistedRecord;
  for (const key of [...TABLE_SESSION_RUNTIME_KEYS, TABLE_SESSION_IDENTITY_KEY]) {
    try {
      hadSessionState ||= window.sessionStorage.getItem(key) !== null;
    } catch {
      // 접근 실패 시에도 best-effort cleanup을 계속한다.
    }
  }

  for (const key of TABLE_SESSION_RUNTIME_KEYS) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // 일부 키 cleanup 실패가 나머지 cleanup을 막지 않는다.
    }
  }
  removeTableSessionIdentity();
  removePersistedTableSession();
  if (hadSessionState) {
    emitTableSessionChanged();
  }
}
